import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { documentsApi, authApi, usersApi, sectorsApi, typesApi, processesApi, useAuth } from '../api';
import ElectronicSignature from '../components/ElectronicSignature';
import StructuredDocumentEditor from '../components/StructuredDocumentEditor';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import SectorFilter, { useSectorFilter } from '../components/SectorFilter';
import { toast } from 'sonner';
import { validateDateFields } from '../utils/dateValidation';
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  FileCheck,
  Archive,
  RefreshCw,
  Upload,
  Download,
  Eye,
  File,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Settings2,
  WandSparkles,
} from 'lucide-react';

const STATUS_LABELS = {
  draft: { label: 'Rascunho', color: 'badge-gray', icon: Clock },
  pending_review: { label: 'Aguardando Revisão', color: 'badge-yellow', icon: Clock },
  pending_approval: { label: 'Aguardando Aprovação', color: 'badge-blue', icon: Clock },
  approved: { label: 'Aprovado', color: 'badge-green', icon: CheckCircle },
  obsolete: { label: 'Obsoleto', color: 'badge-red', icon: Archive },
  cancelled: { label: 'Cancelado', color: 'badge-gray', icon: XCircle },
};

const getTodayLocal = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const isInternalDocument = (documentType = '') => !['external', 'documento externo', 'documentos externos'].includes(documentType.trim().toLocaleLowerCase('pt-BR'));
const TEMPLATE_TYPES = [
  ['procedure', 'Procedimento'], ['instruction', 'Instrução de Trabalho'], ['form', 'Formulário'], ['manual', 'Manual'],
  ['policy', 'Política'], ['specification', 'Especificação'], ['record', 'Registro'], ['other', 'Outro documento interno'],
];
const templateTypeForDocument = (documentType = '') => {
  const key = documentType.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('pt-BR');
  return ({
    procedure: 'procedure', procedimento: 'procedure', procedimentos: 'procedure',
    instruction: 'instruction', 'instrucao de trabalho': 'instruction',
    form: 'form', formulario: 'form', manual: 'manual', policy: 'policy', politica: 'policy',
    specification: 'specification', especificacao: 'specification', record: 'record', registro: 'record',
    other: 'other', outro: 'other', outros: 'other',
  })[key] || '';
};
const documentTypeLabel = (documentType = '') => {
  const templateType = templateTypeForDocument(documentType);
  return TEMPLATE_TYPES.find(([value]) => value === templateType)?.[1] || documentType;
};

const Documents = () => {
  const { user, loading: authLoading } = useAuth();
  const { sectorFilter, setSectorFilter, effectiveFilter } = useSectorFilter(user);

  const [activeTab, setActiveTab] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [history, setHistory] = useState([]);
  const [obsoleteDocs, setObsoleteDocs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [codeConflict, setCodeConflict] = useState(null);
  const [replacementCode, setReplacementCode] = useState('');
  const [reactivateDocument, setReactivateDocument] = useState(null);
  const [reactivateData, setReactivateData] = useState({ admin_email: '', admin_password: '', replacement_code: '', reason: '' });
  const [reactivateConflict, setReactivateConflict] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [signatureDoc, setSignatureDoc] = useState(null);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [docForApproval, setDocForApproval] = useState(null);
  const [approverList, setApproverList] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [revisionReviewerList, setRevisionReviewerList] = useState([]);
  const [selectedReviewerId, setSelectedReviewerId] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    document_type: '',
    sector: '',
    process: '',
    next_review_date: '',
    is_controlled: true,
    template_id: '',
  });
  const [revisionData, setRevisionData] = useState({
    reason: '',
    changes_description: '',
    edit_scope: 'full_document',
    target_section_id: '',
  });
  const [revisionSections, setRevisionSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [alreadyRevised, setAlreadyRevised] = useState(false);
  const [oldVersions, setOldVersions] = useState([]);
  const [currentRevision, setCurrentRevision] = useState('');
  const [currentRevisionDate, setCurrentRevisionDate] = useState('');
  const [structuredDocument, setStructuredDocument] = useState(null);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [codeHistory, setCodeHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [types, setTypes] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const fileInputRef = useRef(null);

  const LIMIT = 20;
  const [docPage, setDocPage] = useState(1);
  const [revPage, setRevPage] = useState(1);
  const [obsPage, setObsPage] = useState(1);
  const [histPage, setHistPage] = useState(1);
  const [docTotal, setDocTotal] = useState(0);
  const [revTotal, setRevTotal] = useState(0);
  const [obsTotal, setObsTotal] = useState(0);
  const [histTotal, setHistTotal] = useState(0);

  const [revFilterSearch, setRevFilterSearch] = useState('');
  const [revFilterStatus, setRevFilterStatus] = useState('');
  const [revFilterReviewer, setRevFilterReviewer] = useState('');
  const [revFilterReviewerList, setRevFilterReviewerList] = useState([]);

  const [obsFilterSearch, setObsFilterSearch] = useState('');
  const [histFilterSearch, setHistFilterSearch] = useState('');
  const [histFilterStatus, setHistFilterStatus] = useState('');

  useEffect(() => {
    // Wait for AuthProvider to resolve the user from localStorage first — otherwise
    // effectiveFilter starts as '' and flips to the user's sector a tick later,
    // firing this effect (and loadData) a second time with the correct filter.
    if (authLoading) return;
    loadData();
  }, [authLoading, activeTab, effectiveFilter, docPage, revPage, obsPage, histPage, filterStatus, filterType, searchTerm, revFilterSearch, revFilterStatus, revFilterReviewer, obsFilterSearch, histFilterSearch, histFilterStatus]);

  useEffect(() => {
    if (authLoading) return;
    loadSectors();
    loadTypes();
    loadProcesses();
    loadUsers();
    loadTemplates();
  }, [authLoading]);

  const templateOptions = useMemo(() => {
    const templateType = templateTypeForDocument(formData.document_type);
    return templateType ? templates.filter(template => template.document_type === templateType) : [];
  }, [formData.document_type, templates]);

  const loadTemplates = async () => {
    try {
      const response = await documentsApi.listTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  };

  const loadTypes = async () => {
    try {
      const response = await typesApi.listActive();
      setTypes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const loadProcesses = async () => {
    try {
      const response = await processesApi.listActive();
      setProcesses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
    }
  };

  const loadSectors = async () => {
    try {
      const response = await sectorsApi.listActive();
      setSectors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersApi.list({ is_active: true });
      setRevFilterReviewerList(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const statsPromise = documentsApi.stats();
      let dataPromise;

      if (activeTab === 'documents') {
        const skip = (docPage - 1) * LIMIT;
        dataPromise = documentsApi.list({
          status: filterStatus || undefined,
          document_type: filterType || undefined,
          sector: effectiveFilter || undefined,
          search: searchTerm || undefined,
          skip,
          limit: LIMIT,
        });
        const [docsRes, statsRes] = await Promise.all([dataPromise, statsPromise]);
        setDocuments(docsRes.data.items);
        setDocTotal(docsRes.data.total);
        setStats(statsRes.data);
      } else if (activeTab === 'revisions') {
        const skip = (revPage - 1) * LIMIT;
        dataPromise = documentsApi.listRevisions({ skip, limit: LIMIT, status: revFilterStatus || undefined, search: revFilterSearch || undefined, reviewer_id: revFilterReviewer || undefined });
        const [revisionsRes, statsRes] = await Promise.all([dataPromise, statsPromise]);
        setRevisions(revisionsRes.data.items);
        setRevTotal(revisionsRes.data.total);
        setStats(statsRes.data);
      } else if (activeTab === 'obsolete') {
        const skip = (obsPage - 1) * LIMIT;
        dataPromise = documentsApi.list({ status: 'obsolete', search: obsFilterSearch || undefined, skip, limit: LIMIT });
        const [obsRes, statsRes] = await Promise.all([dataPromise, statsPromise]);
        setObsoleteDocs(obsRes.data.items);
        setObsTotal(obsRes.data.total);
        setStats(statsRes.data);
      } else if (activeTab === 'history') {
        const skip = (histPage - 1) * LIMIT;
        dataPromise = documentsApi.listHistory({ search: histFilterSearch || undefined, status: histFilterStatus || undefined, skip, limit: LIMIT });
        const [historyRes, statsRes] = await Promise.all([dataPromise, statsPromise]);
        setHistory(historyRes.data.items);
        setHistTotal(historyRes.data.total);
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (doc = null) => {
    if (doc) {
      try {
        const res = await documentsApi.get(doc.id);
        const fullDoc = res.data;
        setEditingDocument(fullDoc);
        setFormData({
          code: fullDoc.code,
          title: fullDoc.title,
          description: fullDoc.description || '',
          document_type: fullDoc.document_type,
          sector: fullDoc.sector || '',
          process: fullDoc.process || '',
          next_review_date: fullDoc.next_review_date || '',
          is_controlled: fullDoc.is_controlled,
          template_id: '',
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do documento');
      }
    } else {
      setEditingDocument(null);
      setFormData({
        code: '',
        title: '',
        description: '',
        document_type: types[0]?.name || '',
        sector: '',
        process: '',
        next_review_date: '',
        is_controlled: true,
        template_id: '',
      });
      setShowModal(true);
    }
    setSelectedFile(null);
    setAlreadyRevised(false);
    setOldVersions([]);
    setCurrentRevision('');
    setCurrentRevisionDate('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDocument(null);
    setSelectedFile(null);
    setAlreadyRevised(false);
    setOldVersions([]);
    setCurrentRevision('');
    setCurrentRevisionDate('');
  };

  const addOldVersion = () => setOldVersions((prev) => [...prev, { file: null, number: '', date: '' }]);
  const updateOldVersion = (index, patch) => setOldVersions((prev) => prev.map((old, i) => (i === index ? { ...old, ...patch } : old)));
  const removeOldVersion = (index) => setOldVersions((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.title) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (!editingDocument && templateOptions.length && !formData.template_id) {
      toast.error('Selecione o modelo-base que define os tópicos do documento');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.next_review_date, label: 'Próxima Revisão' },
    ]);
    if (dateError) { toast.error(dateError); return; }

    if (formData.next_review_date && formData.next_review_date < getTodayLocal()) {
      toast.error('A próxima data de revisão não pode ser anterior à data atual');
      return;
    }

    if (!editingDocument && alreadyRevised) {
      if (!selectedFile) { toast.error('Anexe o arquivo do documento já revisado'); return; }
      if (currentRevision === '' || !currentRevisionDate) {
        toast.error('Informe o número e a data da revisão atual do documento');
        return;
      }
      const incomplete = oldVersions.some((old) => !old.file || old.number === '' || !old.date);
      if (incomplete) { toast.error('Preencha arquivo, revisão e data de cada versão antiga (ou remova a linha)'); return; }
      const numbers = oldVersions.map((old) => Number(old.number));
      if (new Set(numbers).size !== numbers.length) { toast.error('Há versões antigas com o mesmo número de revisão'); return; }
      if (numbers.some((n) => n >= Number(currentRevision))) { toast.error('As versões antigas devem ter revisão menor que a atual'); return; }
      if (oldVersions.some((old) => old.date > currentRevisionDate)) { toast.error('A data de uma versão antiga não pode ser posterior à da revisão atual'); return; }
    }

    setSaving(true);

    // Clean up empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      description: formData.description || null,
      sector: formData.sector || null,
      process: formData.process || null,
      next_review_date: formData.next_review_date || null,
      ...(editingDocument ? {} : { template_id: formData.template_id || null }),
    };

    try {
      let documentId;
      let createdDocument = null;
      if (editingDocument) {
        await documentsApi.update(editingDocument.id, cleanedData);
        documentId = editingDocument.id;
        toast.success('Documento atualizado com sucesso!');
      } else {
        const response = await documentsApi.create(cleanedData);
        documentId = response.data.id;
        createdDocument = response.data;
        toast.success('Documento criado com sucesso!');
      }

      // Only external documents keep an original attachment. Internal content
      // is authored and versioned in the controlled editor.
      if (selectedFile && documentId && !isInternalDocument(cleanedData.document_type)) {
        try {
          setUploadingFile(true);
          await documentsApi.uploadFile(
            documentId,
            selectedFile,
            !editingDocument && alreadyRevised ? { revisionNumber: currentRevision, revisionDate: currentRevisionDate, oldVersions } : {},
          );
          toast.success('Arquivo anexado com sucesso!');
        } catch (error) {
          toast.error('Erro ao anexar arquivo: ' + (error.response?.data?.detail || 'Erro desconhecido'));
        } finally {
          setUploadingFile(false);
        }
      }

      handleCloseModal();
      if (createdDocument && isInternalDocument(createdDocument.document_type)) {
        setStructuredDocument(createdDocument);
      }
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof detail === 'object' ? detail?.message : (detail || 'Erro ao salvar documento'));
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo: 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async (docId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo: 50MB');
        return;
      }

      try {
        setUploadingFile(true);
        await documentsApi.uploadFile(docId, file);
        toast.success('Arquivo anexado com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao anexar arquivo: ' + (error.response?.data?.detail || 'Erro desconhecido'));
      } finally {
        setUploadingFile(false);
      }
    };
    input.click();
  };

  const handleDownloadFile = async (doc) => {
    try {
      const response = await documentsApi.downloadFile(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || `${doc.code}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleViewFile = (doc) => {
    const token = localStorage.getItem('token');
    const viewUrl = documentsApi.viewFile(doc.id);
    window.open(`${viewUrl}?token=${token}`, '_blank');
  };

  const handlePreviewStructured = async (doc) => {
    try {
      const response = await documentsApi.previewContent(doc.id);
      window.open(URL.createObjectURL(response.data), '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preencha e salve o texto antes de visualizar o PDF');
    }
  };

  const handleDownloadHistoryFile = async (historyItem) => {
    try {
      const response = await documentsApi.downloadHistoryFile(historyItem.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = historyItem.file_name || `historico-${historyItem.document_code}-rev${historyItem.revision_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    } catch (error) {
      toast.error('Erro ao baixar arquivo da versão anterior');
    }
  };

  const handleViewHistoryFile = (historyItem) => {
    const token = localStorage.getItem('token');
    const viewUrl = documentsApi.viewHistoryFile(historyItem.id);
    window.open(`${viewUrl}?token=${token}`, '_blank');
  };

  const handleSubmitForApproval = async (doc) => {
    try {
      const res = await usersApi.list({ is_active: true });
      setApproverList((res.data || []).filter(u => u.id !== user?.id));
      setDocForApproval(doc);
      setSelectedApproverId('');
      setShowApproverModal(true);
    } catch (error) {
      toast.error('Erro ao carregar lista de usuários');
    }
  };

  const handleSubmitForApprovalConfirmed = async () => {
    if (!docForApproval || !selectedApproverId) {
      toast.error('Selecione um aprovador');
      return;
    }
    try {
      await documentsApi.submitForApproval(docForApproval.id, selectedApproverId);
      toast.success(`Documento enviado para aprovação!`);
      setShowApproverModal(false);
      setDocForApproval(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para aprovação');
    }
  };

  const handleApprove = (doc) => {
    setSignatureDoc(doc);
  };

  const handleApproveConfirmed = async (userName) => {
    if (!signatureDoc) return;
    try {
      await documentsApi.approve(signatureDoc.id);
      toast.success(`Documento aprovado e assinado por ${userName}`);
      setSignatureDoc(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar documento');
    }
  };

  const handleObsolete = async (doc) => {
    const reason = window.prompt(`Motivo para tornar o documento "${doc.code}" obsoleto:`, 'Documento substituído por nova versão');
    if (!reason) return;

    try {
      await documentsApi.obsolete(doc.id, reason);
      toast.success('Documento marcado como obsoleto!');
      loadData();
    } catch (error) {
      toast.error('Erro ao marcar documento como obsoleto');
    }
  };

  const handleOpenReactivate = (doc) => {
    setReactivateDocument(doc);
    setReactivateConflict(null);
    setReactivateData({ admin_email: '', admin_password: '', replacement_code: '', reason: '' });
  };

  const handleReactivate = async (e) => {
    e.preventDefault();
    if (!reactivateData.admin_email || !reactivateData.admin_password) {
      toast.error('Informe o e-mail e a senha de um administrador');
      return;
    }
    if (reactivateConflict && !reactivateData.replacement_code.trim()) {
      toast.error('Informe o novo código para continuar');
      return;
    }
    setSaving(true);
    try {
      const loginResponse = await authApi.login(reactivateData.admin_email, reactivateData.admin_password);
      if (!loginResponse.data.user?.is_admin) {
        toast.error('As credenciais informadas não pertencem a um administrador');
        return;
      }
      await documentsApi.reactivate(
        reactivateDocument.id,
        {
          replacement_code: reactivateData.replacement_code.trim() || null,
          reason: reactivateData.reason.trim() || null,
        },
        loginResponse.data.access_token,
      );
      toast.success('Documento reativado e aprovado pelo administrador');
      setReactivateDocument(null);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'non_obsolete_document_code_conflict') {
        setReactivateConflict(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Não foi possível reativar o documento');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRequestRevision = async (doc) => {
    try {
      const [res, content] = await Promise.all([
        usersApi.list({ is_active: true }),
        isInternalDocument(doc.document_type) ? documentsApi.getContent(doc.id) : Promise.resolve({ data: { sections: [] } }),
      ]);
      setRevisionReviewerList((res.data || []).filter(u => u.id !== user?.id));
      setSelectedDocument(doc);
      setRevisionSections(content.data.sections || []);
      setRevisionData({ reason: '', changes_description: '', edit_scope: 'full_document', target_section_id: '' });
      setSelectedReviewerId('');
      setCodeConflict(null);
      setReplacementCode('');
      setShowRevisionModal(true);
    } catch (error) {
      toast.error('Erro ao carregar lista de usuários');
    }
  };

  const submitRevisionRequest = async (replacement_code = '') => {
    setSaving(true);
    try {
      await documentsApi.createRevision({
        document_id: selectedDocument.id,
        reviewer_id: selectedReviewerId,
        ...revisionData,
        ...(replacement_code ? { replacement_code } : {}),
      });
      toast.success('Solicitação de revisão criada!');
      setShowRevisionModal(false);
      setCodeConflict(null);
      setReplacementCode('');
      loadData();
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'non_obsolete_document_code_conflict') {
        setCodeConflict(detail);
        setReplacementCode('');
        setShowRevisionModal(false);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao criar solicitação de revisão');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRevision = async (e) => {
    e.preventDefault();
    if (!revisionData.reason) {
      toast.error('Informe o motivo da revisão');
      return;
    }

    if (!selectedReviewerId) {
      toast.error('Selecione um revisor');
      return;
    }
    if (revisionData.edit_scope === 'topic' && !revisionData.target_section_id) {
      toast.error('Selecione o tópico a ser revisado');
      return;
    }
    await submitRevisionRequest();
  };

  const handleConfirmReplacementCode = async () => {
    const code = replacementCode.trim();
    if (!code) {
      toast.error('Informe o novo código do documento');
      return;
    }
    await submitRevisionRequest(code);
  };

  const handleCompleteRevision = async (revision) => {
    if (!window.confirm('Concluir esta revisão?')) return;

    try {
      await documentsApi.completeRevision(revision.id);
      toast.success('Revisão concluída! Documento retornou para a aba Documentos.');
      setActiveTab('documents');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir revisão');
    }
  };

  const handleDelete = async (doc) => {
    const reason = window.prompt(
      `Informe o motivo para cancelar o documento "${doc.code} - ${doc.title}":\n(F4-07 — ISO 13485: justificativa obrigatória para rastreabilidade)`,
      ''
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('É obrigatório informar o motivo da exclusão'); return; }
    try {
      await documentsApi.delete(doc.id, { deletion_reason: reason });
      toast.success('Documento cancelado com justificativa registrada');
      loadData();
    } catch (error) {
      toast.error('Erro ao cancelar documento');
    }
  };

  const handleOpenStructuredEditor = (doc) => {
    setStructuredDocument(doc);
  };

  const handleCloseStructuredEditor = () => {
    setStructuredDocument(null);
    loadData();
  };

  const handleOpenVersionHistory = async (doc) => {
    try {
      setVersionHistoryDoc(doc);
      setLoadingVersions(true);
      const [versionsResponse, codeHistoryResponse] = await Promise.all([
        documentsApi.listVersions(doc.id),
        documentsApi.listCodeHistory(doc.id),
      ]);
      setDocumentVersions(versionsResponse.data || []);
      setCodeHistory(codeHistoryResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar versões');
      setVersionHistoryDoc(null);
      setCodeHistory([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    if (!versionHistoryDoc) return;
    try {
      const response = await documentsApi.downloadVersion(versionHistoryDoc.id, version.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = version.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar versão');
    }
  };

  const tabs = [
    { id: 'documents', label: 'Documentos', icon: FileText },
    { id: 'revisions', label: 'Revisões', icon: RefreshCw },
    { id: 'obsolete', label: 'Obsoletos', icon: Archive },
    { id: 'history', label: 'Histórico', icon: Clock },
  ];

  const COLUMNS = [
    { key: 'codigo', label: 'Código', locked: true, render: (doc) => <td className="font-medium text-white">{doc.code}</td> },
    { key: 'titulo', label: 'Título', render: (doc) => <td className="max-w-xs truncate">{doc.title}</td> },
    { key: 'tipo', label: 'Tipo', render: (doc) => <td>{documentTypeLabel(doc.document_type)}</td> },
    { key: 'setor', label: 'Setor', render: (doc) => <td>{doc.sector || '-'}</td> },
    { key: 'revisao', label: 'Revisão', headerClass: 'text-center', render: (doc) => <td className="text-center">{doc.current_revision}</td> },
    { key: 'data_revisao', label: 'Data Revisão', headerClass: 'text-center', render: (doc) => (
      <td className="text-center text-sm text-gray-400">
        {doc.revision_date ? new Date(doc.revision_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
      </td>
    ) },
    { key: 'arquivo', label: 'Arquivo', render: (doc) => (
      <td>
        {isInternalDocument(doc.document_type) ? (
          <div className="flex items-center gap-1"><button onClick={() => handlePreviewStructured(doc)} className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg flex items-center gap-1" title="Visualizar PDF gerado pelo modelo-base"><Eye size={16} /><span className="text-xs">Visualizar</span></button><button onClick={() => handleDownloadFile(doc)} className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg flex items-center gap-1" title="Baixar PDF gerado pelo modelo-base"><Download size={16} /><span className="text-xs">Baixar PDF</span></button></div>
        ) : doc.has_file ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleViewFile(doc)}
              className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg flex items-center gap-1"
              title="Visualizar documento (sem download)"
            >
              <Eye size={16} />
              <span className="text-xs">Ver</span>
            </button>
            <button
              onClick={() => handleDownloadFile(doc)}
              className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg flex items-center gap-1"
              title="Baixar documento"
            >
              <Download size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleUploadFile(doc.id)}
            className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg flex items-center gap-1"
            title="Anexar arquivo"
          >
            <Upload size={16} />
            <span className="text-xs">Anexar</span>
          </button>
        )}
      </td>
    ) },
    { key: 'status', label: 'Status', render: (doc) => {
      const statusInfo = STATUS_LABELS[doc.status] || STATUS_LABELS.draft;
      const StatusIcon = statusInfo.icon;
      return (
        <td>
          <span className={`badge ${statusInfo.color} flex items-center gap-1 w-fit`}>
            <StatusIcon size={14} />
            {statusInfo.label}
          </span>
        </td>
      );
    } },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (doc) => (
      <td>
        <div className="flex items-center justify-end gap-1">
          {doc.status === 'draft' && user?.id && (doc.author_id === user.id || !doc.author_id) && (
            <button
              onClick={() => handleSubmitForApproval(doc)}
              className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
              title="Enviar para Aprovação"
            >
              <FileCheck size={18} />
            </button>
          )}
          {doc.status === 'pending_approval' && (
            doc.approver_id === user?.id ? (
              <button
                onClick={() => handleApprove(doc)}
                className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg"
                title="Aprovar Documento"
              >
                <CheckCircle size={18} />
              </button>
            ) : (
              <span className="text-xs text-gray-500 px-2 py-1" title={doc.approver_name ? `Aguardando aprovação de ${doc.approver_name}` : ''}>
                <Clock size={14} className="inline mr-1" />
                {doc.approver_name ? `${doc.approver_name}` : 'Aguardando'}
              </span>
            )
          )}
          {doc.status === 'approved' && (
            <>
              <button
                onClick={() => handleRequestRevision(doc)}
                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                title="Solicitar Revisão"
              >
                <RefreshCw size={18} />
              </button>
              {doc.status === 'approved' && <button
                  onClick={() => handleObsolete(doc)}
                  className="p-2 text-gray-400 hover:text-orange-400 hover:bg-gray-700 rounded-lg"
                  title="Marcar como Obsoleto"
                  data-testid="obsolete-btn"
                >
                  <Archive size={18} />
                </button>}
            </>
          )}
          {doc.status === 'obsolete' && (
            <button
              onClick={() => handleOpenReactivate(doc)}
              className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
              title="Reativar documento (autenticação de administrador)"
              data-testid="reactivate-btn"
            >
              <RefreshCw size={18} />
            </button>
          )}
          <button
            onClick={() => handleOpenModal(doc)}
            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
            title="Editar"
          >
            <Edit size={18} />
          </button>
          {doc.status !== 'obsolete' && (
            <button
              onClick={() => handleDelete(doc)}
              className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
              title="Cancelar"
            >
              <Trash2 size={18} />
            </button>
          )}
          <button
            onClick={() => handleOpenVersionHistory(doc)}
            className="p-2 text-gray-400 hover:text-purple-400 hover:bg-gray-700 rounded-lg"
            title="Histórico de versões"
          >
            <Clock size={18} />
          </button>
          {isInternalDocument(doc.document_type) && user?.id && (
            (doc.status === 'draft' && doc.author_id === user.id) ||
            (doc.status === 'pending_review' && doc.reviewer_id === user.id)
          ) && (
            <button
              onClick={() => handleOpenStructuredEditor(doc)}
              className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
              title="Editar conteúdo estruturado"
            >
              <Edit size={18} />
            </button>
          )}
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'documents', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="text-green-500" />
            Gestão de Documentos
          </h1>
          <p className="text-gray-400 mt-1">Controle de documentos do SGQ</p>
        </div>
        {activeTab === 'documents' && (
          <div className="flex flex-wrap gap-2">
            {user?.is_admin && <>
              <button onClick={() => setShowImportDialog(true)} className="btn-secondary flex items-center gap-2"><WandSparkles size={18} /> Importar com IA</button>
              <button onClick={() => setShowTemplateManager(true)} className="btn-secondary flex items-center gap-2"><Settings2 size={18} /> Modelos</button>
            </>}
            <button onClick={() => handleOpenModal()} className="btn-primary flex items-center gap-2" data-testid="add-document-btn"><Plus size={20} /> Novo Documento</button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && activeTab === 'documents' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Aprovados</p>
            <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Rascunhos</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.draft}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Revisões Pendentes</p>
            <p className="text-2xl font-bold text-blue-400">{stats.pending_revisions}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Obsoletos</p>
            <p className="text-2xl font-bold text-red-400">{stats.obsolete || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.id
              ? 'text-green-400 border-green-400'
              : 'text-gray-400 border-transparent hover:text-white'
              }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      {activeTab === 'documents' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setDocPage(1); }}
              className="input pl-10"
            />
          </div>
          <SectorFilter
            user={user}
            value={sectorFilter}
            onChange={(val) => { setSectorFilter(val); }}
            sectors={sectors}
            className="input px-3 w-full sm:w-60"
          />
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setDocPage(1); }}
            className="input px-3 w-full sm:w-60"
          >
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setDocPage(1); }}
            className="input px-3 w-full sm:w-60"
          >
            <option value="">Todos os Tipos</option>
            {types.map(type => (
              <option key={type.id} value={type.name}>{type.name}</option>
            ))}
          </select>
          <div className="flex items-center">
            <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          </div>
        </div>
      )}

      {/* Revisions Filters */}
      {activeTab === 'revisions' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código ou título do documento..."
              value={revFilterSearch}
              onChange={(e) => { setRevFilterSearch(e.target.value); setRevPage(1); }}
              className="input pl-10"
            />
          </div>
          <select
            value={revFilterStatus}
            onChange={(e) => { setRevFilterStatus(e.target.value); setRevPage(1); }}
            className="input px-3 w-full sm:w-48"
          >
            <option value="">Todos os Status</option>
            <option value="pending">Pendente</option>
            <option value="in_progress">Em Andamento</option>
            <option value="completed">Concluída</option>
            <option value="cancelled">Cancelada</option>
          </select>
          <select
            value={revFilterReviewer}
            onChange={(e) => { setRevFilterReviewer(e.target.value); setRevPage(1); }}
            className="input px-3 w-full sm:w-48"
          >
            <option value="">Todos os Revisores</option>
            {revFilterReviewerList.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Obsolete Search */}
      {activeTab === 'obsolete' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar documentos obsoletos..."
              value={obsFilterSearch}
              onChange={(e) => { setObsFilterSearch(e.target.value); setObsPage(1); }}
              className="input pl-10"
            />
          </div>
          <div className="flex items-center">
            <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          </div>
        </div>
      )}

      {/* History Filters */}
      {activeTab === 'history' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Filtrar por código ou título..."
              value={histFilterSearch}
              onChange={(e) => { setHistFilterSearch(e.target.value); setHistPage(1); }}
              className="input pl-10"
            />
          </div>
          <select
            value={histFilterStatus}
            onChange={(e) => { setHistFilterStatus(e.target.value); setHistPage(1); }}
            className="input px-3 w-full sm:w-48"
          >
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Content */}
      <div className="card overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-gray-900/60 flex items-center justify-center z-10">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        )}
        {activeTab === 'documents' ? (
          <>
            <DocumentsTable
              documents={documents}
              visibleColumns={visibleColumns}
              onEdit={handleOpenModal}
              onDelete={handleDelete}
              onSubmitForApproval={handleSubmitForApproval}
              onApprove={handleApprove}
              onObsolete={handleObsolete}
              onRequestRevision={handleRequestRevision}
              onReactivate={handleOpenReactivate}
              onViewFile={handleViewFile}
              onDownloadFile={handleDownloadFile}
              onUploadFile={handleUploadFile}
              onEditStructured={handleOpenStructuredEditor}
              onPreviewStructured={handlePreviewStructured}
              onVersionHistory={handleOpenVersionHistory}
            />
            <Pagination page={docPage} total={docTotal} limit={LIMIT} onPageChange={setDocPage} />
          </>
        ) : activeTab === 'revisions' ? (
          <>
            <RevisionsTable
              revisions={revisions}
              currentUserId={user?.id}
              onComplete={handleCompleteRevision}
              onViewFile={handleViewFile}
              onDownloadFile={handleDownloadFile}
              onUploadFile={handleUploadFile}
            />
            <Pagination page={revPage} total={revTotal} limit={LIMIT} onPageChange={setRevPage} />
          </>
        ) : activeTab === 'obsolete' ? (
          <>
            <DocumentsTable
              documents={obsoleteDocs}
              visibleColumns={visibleColumns}
              onEdit={handleOpenModal}
              onDelete={handleDelete}
              onRequestRevision={handleRequestRevision}
              onReactivate={handleOpenReactivate}
              onViewFile={handleViewFile}
              onDownloadFile={handleDownloadFile}
              onUploadFile={handleUploadFile}
              onEditStructured={handleOpenStructuredEditor}
              onPreviewStructured={handlePreviewStructured}
              onVersionHistory={handleOpenVersionHistory}
            />
            <Pagination page={obsPage} total={obsTotal} limit={LIMIT} onPageChange={setObsPage} />
          </>
        ) : (
          <>
            <HistoryTable
              history={history}
              onViewFile={handleViewHistoryFile}
              onDownloadFile={handleDownloadHistoryFile}
            />
            <Pagination page={histPage} total={histTotal} limit={LIMIT} onPageChange={setHistPage} />
          </>
        )}
      </div>

      {/* Document Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingDocument ? 'Editar Documento' : 'Novo Documento'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="input"
                    placeholder="Ex: PQ-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo *</label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value, template_id: '' })}
                    className="input"
                  >
                    {types.map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!editingDocument && templateTypeForDocument(formData.document_type) && (
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
                  <label className="block text-sm font-medium text-blue-100 mb-2">Modelo-base *</label>
                  {templateOptions.length ? <>
                    <select value={formData.template_id} onChange={(e) => setFormData({ ...formData, template_id: e.target.value })} className="input">
                      <option value="">Selecione o modelo que define os tópicos</option>
                      {templateOptions.map(template => <option key={template.id} value={template.id}>{template.name} · v{template.version_number} · {template.default_sections.length} tópicos</option>)}
                    </select>
                    <p className="mt-2 text-xs text-blue-200">O modelo escolhido define o papel timbrado, os tópicos, as fontes e as cores deste documento.</p>
                  </> : <p className="text-sm text-blue-100">Nenhum modelo ativo foi cadastrado para este tipo. O sistema usará o modelo padrão.</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input"
                  placeholder="Título do documento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input min-h-[80px]"
                  placeholder="Descrição do documento"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Setor</label>
                  <select
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="input"
                    data-testid="sector-select"
                  >
                    <option value="">Selecione um setor</option>
                    {sectors.map(sector => (
                      <option key={sector.id} value={sector.name}>{sector.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Processo</label>
                  <select
                    value={formData.process}
                    onChange={(e) => setFormData({ ...formData, process: e.target.value })}
                    className="input"
                  >
                    <option value="">Selecione um processo</option>
                    {processes.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Próxima Revisão</label>
                  <input
                    type="date"
                    value={formData.next_review_date}
                    onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                    className="input"
                    min={getTodayLocal()}
                    max="2099-12-31"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_controlled}
                      onChange={(e) => setFormData({ ...formData, is_controlled: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                    />
                    <span className="text-sm font-medium text-gray-300">Documento Controlado</span>
                  </label>
                </div>
              </div>

              {/* External documents retain the source attachment; internal ones are composed in the editor. */}
              {!isInternalDocument(formData.document_type) ? <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Upload size={16} className="inline mr-2" />
                  Arquivo do Documento
                </label>
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-green-500 transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-400">
                        <File size={20} />
                        <span>{selectedFile.name}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setSelectedFile(null); }}
                          className="text-red-400 hover:text-red-300 ml-2"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        <Upload size={32} className="mx-auto mb-2" />
                        <p>Clique para selecionar ou arraste um arquivo</p>
                        <p className="text-xs mt-1">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, PNG, JPG (máx. 50MB)</p>
                      </div>
                    )}
                  </label>
                </div>
                {editingDocument?.file_name && !selectedFile && (
                  <p className="text-sm text-gray-400 mt-2">
                    Arquivo atual: <span className="text-green-400">{editingDocument.file_name}</span>
                  </p>
                )}
                {!editingDocument && user?.is_admin && (
                  <div className="mt-3 rounded-lg border border-gray-600 p-4 space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={alreadyRevised}
                        onChange={(e) => setAlreadyRevised(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                      />
                      <span className="text-sm font-medium text-gray-300">Documento já revisado (lançar na revisão atual, já aprovado)</span>
                    </label>
                    {alreadyRevised && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Revisão atual *</label>
                          <input
                            type="number"
                            min="0"
                            max="999"
                            value={currentRevision}
                            onChange={(e) => setCurrentRevision(e.target.value)}
                            className="input"
                            placeholder="Ex: 3"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Data da revisão *</label>
                          <input
                            type="date"
                            value={currentRevisionDate}
                            onChange={(e) => setCurrentRevisionDate(e.target.value)}
                            className="input"
                            max={getTodayLocal()}
                          />
                        </div>
                        <p className="md:col-span-2 text-xs text-gray-400">
                          A data da próxima revisão é a do campo "Próxima Revisão" acima.
                        </p>
                        <div className="md:col-span-2 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-300">Versões antigas (opcional)</span>
                            <button type="button" onClick={addOldVersion} className="btn-secondary text-sm">+ Adicionar versão antiga</button>
                          </div>
                          {oldVersions.map((old, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_110px_160px_auto] gap-3 items-end rounded-lg border border-gray-700 p-3">
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Arquivo</label>
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                                  onChange={(e) => {
                                    const f = e.target.files[0];
                                    if (f && f.size > 50 * 1024 * 1024) { toast.error('Arquivo muito grande. Máximo: 50MB'); e.target.value = ''; return; }
                                    updateOldVersion(index, { file: f || null });
                                  }}
                                  className="input text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Revisão</label>
                                <input type="number" min="0" max="998" value={old.number} onChange={(e) => updateOldVersion(index, { number: e.target.value })} className="input" placeholder="Ex: 1" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Data da revisão</label>
                                <input type="date" value={old.date} max={currentRevisionDate || getTodayLocal()} onChange={(e) => updateOldVersion(index, { date: e.target.value })} className="input" />
                              </div>
                              <button type="button" onClick={() => removeOldVersion(index)} className="text-red-400 hover:text-red-300 p-2" title="Remover"><X size={18} /></button>
                            </div>
                          ))}
                        </div>

                      </div>
                    )}
                  </div>
                )}
              </div> : <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-100"><FileText size={16} className="inline mr-2" />O conteúdo deste documento será criado e controlado pelo sistema após o cadastro.</div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving || uploadingFile}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving || uploadingFile}>
                  {saving || uploadingFile ? <><Loader2 size={18} className="animate-spin" /> {uploadingFile ? 'Enviando arquivo...' : 'Salvando...'}</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Revision Modal */}
      {showRevisionModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Solicitar Revisão</h2>
              <button onClick={() => setShowRevisionModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <p className="text-gray-400 mb-4">
              Documento: <span className="text-white font-medium">{selectedDocument?.code} - {selectedDocument?.title}</span>
            </p>

            <form onSubmit={handleSubmitRevision} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Selecione o Revisor *</label>
                <select
                  value={selectedReviewerId}
                  onChange={(e) => setSelectedReviewerId(e.target.value)}
                  className="input"
                >
                  <option value="">Selecione um usuário</option>
                  {revisionReviewerList.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} {u.department ? `— ${u.department}` : ''}</option>
                  ))}
                </select>
              </div>

              {selectedDocument && isInternalDocument(selectedDocument.document_type) && <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Escopo da edição *</label>
                <label className="flex items-center gap-2 text-sm text-gray-200"><input type="radio" checked={revisionData.edit_scope === 'full_document'} onChange={() => setRevisionData({ ...revisionData, edit_scope: 'full_document', target_section_id: '' })} /> Redefinir o documento inteiro</label>
                <label className="flex items-center gap-2 text-sm text-gray-200"><input type="radio" checked={revisionData.edit_scope === 'topic'} onChange={() => setRevisionData({ ...revisionData, edit_scope: 'topic' })} /> Editar somente um tópico</label>
                {revisionData.edit_scope === 'topic' && <select value={revisionData.target_section_id} onChange={(e) => setRevisionData({ ...revisionData, target_section_id: e.target.value })} className="input"><option value="">Selecione o tópico</option>{revisionSections.map(section => <option key={section.section_id} value={section.section_id}>{section.title}</option>)}</select>}
              </div>}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Motivo da Revisão *</label>
                <textarea
                  value={revisionData.reason}
                  onChange={(e) => setRevisionData({ ...revisionData, reason: e.target.value })}
                  className="input min-h-[100px]"
                  placeholder="Descreva o motivo da revisão"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Alterações Propostas</label>
                <textarea
                  value={revisionData.changes_description}
                  onChange={(e) => setRevisionData({ ...revisionData, changes_description: e.target.value })}
                  className="input min-h-[80px]"
                  placeholder="Descreva as alterações necessárias"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowRevisionModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Enviando...</> : 'Solicitar Revisão'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {reactivateDocument && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">Reativar documento</h2>
              <button onClick={() => setReactivateDocument(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              A reativação torna o documento vigente novamente e exige autenticação de um administrador.
            </p>
            <form onSubmit={handleReactivate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Login do administrador *</label>
                <input type="email" value={reactivateData.admin_email} onChange={(e) => setReactivateData({ ...reactivateData, admin_email: e.target.value })} className="input" autoFocus autoComplete="username" placeholder="admin@empresa.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Senha do administrador *</label>
                <input type="password" value={reactivateData.admin_password} onChange={(e) => setReactivateData({ ...reactivateData, admin_password: e.target.value })} className="input" autoComplete="current-password" placeholder="••••••••" />
              </div>
              {reactivateConflict && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100">
                    <p className="font-medium text-amber-200">O código atual já está sendo usado.</p>
                    <p className="mt-1">{reactivateConflict.conflicting_document?.code} — {reactivateConflict.conflicting_document?.title || 'Documento não obsoleto'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">Novo código *</label>
                    <input value={reactivateData.replacement_code} onChange={(e) => setReactivateData({ ...reactivateData, replacement_code: e.target.value })} className="input" maxLength={50} autoFocus placeholder={reactivateDocument.code} />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Justificativa</label>
                <textarea value={reactivateData.reason} onChange={(e) => setReactivateData({ ...reactivateData, reason: e.target.value })} className="input min-h-[80px]" placeholder="Motivo da reativação" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setReactivateDocument(null)} className="btn-secondary" disabled={saving}>Cancelar</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Validando...</> : 'Autenticar e reativar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* The original code was reused while this document was obsolete. */}
      {codeConflict && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">Alterar código para revisar</h2>
              <button onClick={() => setCodeConflict(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>

            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100 mb-4">
              <p className="font-medium text-amber-200">O código {selectedDocument?.code} já está em uso.</p>
              <p className="mt-1">{codeConflict.conflicting_document?.code} — {codeConflict.conflicting_document?.title || 'Documento não obsoleto'}</p>
            </div>

            <p className="text-sm text-gray-300 mb-4">
              Para retornar este documento obsoleto à revisão, informe um novo código. O código anterior, o novo código, data e responsável ficarão registrados no histórico deste documento.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Novo código *</label>
              <input
                value={replacementCode}
                onChange={(e) => setReplacementCode(e.target.value)}
                className="input"
                maxLength={50}
                autoFocus
                placeholder="Ex.: PQ-001-R1"
              />
            </div>

            <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-700">
              <button type="button" onClick={() => { setCodeConflict(null); setShowRevisionModal(true); }} className="btn-secondary" disabled={saving}>Voltar</button>
              <button type="button" onClick={handleConfirmReplacementCode} className="btn-primary flex items-center gap-2" disabled={saving}>
                {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Alterar e solicitar revisão'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Approver Selection Modal */}
      {showApproverModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Enviar para Aprovação</h2>
              <button onClick={() => setShowApproverModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <p className="text-gray-400 mb-4">
              Documento: <span className="text-white font-medium">{docForApproval?.code} - {docForApproval?.title}</span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Selecione o Aprovador *</label>
                <select
                  value={selectedApproverId}
                  onChange={(e) => setSelectedApproverId(e.target.value)}
                  className="input"
                >
                  <option value="">Selecione um usuário</option>
                  {approverList.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} {u.department ? `— ${u.department}` : ''}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowApproverModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="button" onClick={handleSubmitForApprovalConfirmed} className="btn-primary flex items-center gap-2">
                  <FileCheck size={16} /> Enviar para Aprovação
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* F4-06: Electronic Signature for document approval */}
      <ElectronicSignature
        isOpen={!!signatureDoc}
        onClose={() => setSignatureDoc(null)}
        onConfirm={handleApproveConfirmed}
        title={`Aprovar Documento${signatureDoc ? ` — ${signatureDoc.code}` : ''}`}
        description={signatureDoc ? `Você está prestes a aprovar o documento "${signatureDoc.title}". Confirme sua identidade para prosseguir.` : ''}
      />

      {structuredDocument && (
        <StructuredDocumentEditor
          document={structuredDocument}
          onClose={handleCloseStructuredEditor}
          onSaved={() => {
            toast.success('Conteúdo salvo e PDF com timbrado gerado');
            loadData();
          }}
        />
      )}

      {showTemplateManager && <TemplateManager onClose={() => { setShowTemplateManager(false); loadTemplates(); }} />}
      {showImportDialog && <AiImportDialog onClose={() => { setShowImportDialog(false); loadData(); }} />}

      {versionHistoryDoc && (
        <VersionHistoryDialog
          document={versionHistoryDoc}
          versions={documentVersions}
          codeHistory={codeHistory}
          loading={loadingVersions}
          onClose={() => setVersionHistoryDoc(null)}
          onDownload={handleDownloadVersion}
        />
      )}
    </div>
  );
};

const DocumentsTable = ({ documents, visibleColumns }) => {
  return (
  <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          {visibleColumns.map(col => (
            <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {documents.length === 0 ? (
          <tr>
            <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">
              Nenhum documento encontrado
            </td>
          </tr>
        ) : (
          documents.map(doc => (
            <tr key={doc.id}>
              {visibleColumns.map(col => (
                <Fragment key={col.key}>{col.render(doc)}</Fragment>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
  );
};

const VersionHistoryDialog = ({ document, versions, codeHistory, loading, onClose, onDownload }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
    <div className="card w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-white">Versões do documento</h2>
          <p className="text-sm text-gray-400">{document.code} — {document.title}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      <div className="overflow-auto">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-green-400" /></div>
        ) : (
          <>
          {versions.length === 0 ? <p className="p-8 text-center text-gray-500">Nenhuma versão encontrada.</p> : (
          <table className="table">
            <thead><tr><th>Versão</th><th>Estado</th><th>Origem</th><th>Salva por</th><th>Data</th><th className="text-right">Arquivo</th></tr></thead>
            <tbody>
              {versions.map(version => (
                <tr key={version.id}>
                  <td className="font-mono">{version.version_number}</td>
                  <td><span className={`badge ${version.status === 'approved' ? 'badge-green' : version.status === 'obsolete' ? 'badge-gray' : 'badge-yellow'}`}>{version.status === 'approved' ? 'Vigente' : version.status === 'obsolete' ? 'Obsoleta' : 'Rascunho'}</span></td>
                  <td>{version.source === 'structured_pdf' ? 'Editor controlado' : version.source === 'editor' ? 'Editor' : version.source === 'migration' ? 'Migração' : 'Upload'}</td>
                  <td>{version.created_by_name || '-'}</td>
                  <td>{new Date(version.created_at).toLocaleString('pt-BR')}</td>
                  <td className="text-right"><button onClick={() => onDownload(version)} className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg" title="Baixar versão"><Download size={17} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          {codeHistory.length > 0 && (
            <div className="border-t border-gray-700 p-4">
              <h3 className="font-medium text-white mb-3">Rastreabilidade de códigos</h3>
              <div className="space-y-3">
                {codeHistory.map(entry => (
                  <div key={entry.id} className="rounded-lg bg-gray-800/70 border border-gray-700 p-3 text-sm">
                    <p className="text-white"><span className="font-mono text-red-300">{entry.previous_code}</span><span className="text-gray-500 mx-2">→</span><span className="font-mono text-green-300">{entry.new_code}</span></p>
                    <p className="text-gray-300 mt-1">{entry.reason}</p>
                    <p className="text-xs text-gray-500 mt-1">{entry.changed_by_name || 'Usuário do sistema'} · {new Date(entry.changed_at).toLocaleString('pt-BR')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  </div>
);

const RevisionsTable = ({ revisions, currentUserId, onComplete, onViewFile, onDownloadFile, onUploadFile }) => (
  <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Documento</th>
          <th>Título</th>
          <th title="Número da revisão ao ser concluída">Rev. Alvo</th>
          <th>Motivo</th>
          <th>Solicitante</th>
          <th>Revisor</th>
          <th>Data</th>
          <th>Documento Atual</th>
          <th>Status</th>
          <th className="text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        {revisions.length === 0 ? (
          <tr>
            <td colSpan="10" className="text-center py-8 text-gray-500">
              Nenhuma solicitação de revisão
            </td>
          </tr>
        ) : (
          revisions.map(rev => (
            <tr key={rev.id}>
              <td className="font-medium text-white">{rev.document_code}</td>
              <td className="max-w-xs truncate">{rev.document_title}</td>
              <td className="text-center">{rev.revision_number}</td>
              <td className="max-w-xs whitespace-normal break-words">{rev.reason}</td>
              <td>{rev.requested_by_name}</td>
              <td>{rev.reviewer_name || '-'}</td>
              <td>{new Date(rev.requested_date).toLocaleDateString('pt-BR')}</td>
              <td>
                {rev.document_has_file ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onViewFile({ id: rev.document_id, code: rev.document_code })}
                      className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg flex items-center gap-1"
                      title="Visualizar documento atual"
                    >
                      <Eye size={16} />
                      <span className="text-xs">Ver</span>
                    </button>
                    <button
                      onClick={() => onDownloadFile({ id: rev.document_id, code: rev.document_code, file_name: rev.document_file_name })}
                      className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg"
                      title="Baixar documento atual"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">Sem arquivo</span>
                )}
              </td>
              <td>
                <span className={`badge ${rev.status === 'pending' ? 'badge-yellow' :
                  rev.status === 'in_progress' ? 'badge-blue' :
                    rev.status === 'completed' ? 'badge-green' : 'badge-gray'
                  }`}>
                  {rev.status === 'pending' ? 'Pendente' :
                    rev.status === 'in_progress' ? 'Em Andamento' :
                      rev.status === 'completed' ? 'Concluída' : 'Cancelada'}
                </span>
              </td>
              <td>
                <div className="flex items-center justify-end gap-1">
                  {rev.status === 'pending' && currentUserId && rev.reviewer_id === currentUserId && (
                    <>
                      <button
                        onClick={() => onUploadFile(rev.document_id)}
                        className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg"
                        title="Enviar nova versão do documento"
                      >
                        <Upload size={18} />
                      </button>
                      <button
                        onClick={() => onComplete(rev)}
                        className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                        title="Concluir Revisão"
                      >
                        <CheckCircle size={18} />
                      </button>
                    </>
                  )}
                  {rev.status === 'pending' && currentUserId && rev.reviewer_id !== currentUserId && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      Aguardando revisor
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const HistoryTable = ({ history, onViewFile, onDownloadFile }) => {
  const [expandedCodes, setExpandedCodes] = useState({});

  const grouped = useMemo(() => {
    const map = {};
    history.forEach(h => {
      if (!map[h.document_code]) map[h.document_code] = [];
      map[h.document_code].push(h);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [history]);

  useEffect(() => {
    setExpandedCodes(prev => {
      const next = { ...prev };
      grouped.forEach(([code]) => {
        if (next[code] === undefined) next[code] = true;
      });
      return next;
    });
  }, [grouped]);

  const toggle = (code) =>
    setExpandedCodes(prev => ({ ...prev, [code]: !prev[code] }));

  return (
    <div className="space-y-3 p-4">
      {/* Estado vazio */}
      {grouped.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Nenhum histórico de revisões
        </div>
      )}

      {/* Grupos por documento */}
      {grouped.map(([code, entries]) => (
        <div key={code} className="border border-gray-700 rounded-lg overflow-hidden">
          {/* Cabeçalho do grupo */}
          <button
            onClick={() => toggle(code)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={16} className="text-green-500 shrink-0" />
              <span className="font-semibold text-white shrink-0">{code}</span>
              <span className="text-gray-400 text-sm truncate hidden sm:block">
                — {entries[0].title}
              </span>
              <span className="bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded-full shrink-0">
                {entries.length} rev.
              </span>
              {entries[0].status && (
                <span className={`badge ${STATUS_LABELS[entries[0].status]?.color || 'badge-gray'} shrink-0`}>
                  {STATUS_LABELS[entries[0].status]?.label || entries[0].status}
                </span>
              )}
            </div>
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform shrink-0 ml-2 ${expandedCodes[code] ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Linhas do grupo */}
          {expandedCodes[code] && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Revisão</th>
                    <th>Título</th>
                    <th>Motivo</th>
                    <th>Data</th>
                    <th>Por</th>
                    <th>Arquivo</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(h => (
                    <tr key={h.id}>
                      <td className="text-center font-mono w-20">{h.revision_number}</td>
                      <td className="max-w-xs truncate">{h.title}</td>
                      <td className="max-w-xs truncate text-gray-400">{h.obsolete_reason || '-'}</td>
                      <td className="whitespace-nowrap">
                        {new Date(h.obsolete_date).toLocaleDateString('pt-BR')}
                      </td>
                      <td>{h.obsoleted_by_name || '-'}</td>
                    <td>
                      {h.file_path && h.file_name ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onViewFile(h)}
                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg flex items-center gap-1"
                            title="Visualizar"
                          >
                            <Eye size={16} />
                            <span className="text-xs">Ver</span>
                          </button>
                          <button
                            onClick={() => onDownloadFile(h)}
                            className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg flex items-center gap-1"
                            title="Baixar"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const TemplateManager = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingTemplateId, setUploadingTemplateId] = useState(null);
  const [form, setForm] = useState({ document_type: 'procedure', name: 'Modelo de Procedimento', company_name: '', footer_text: 'Cópia controlada — impressão não controlada', primary_color: '#14532d', body_color: '#1f2937', font_family: 'Arial', heading_font_family: 'Arial', default_sections: 'Objetivo\nAbrangência\nResponsabilidades\nReferências\nDescrição do procedimento\nRegistros\nAnexos' });
  const load = () => documentsApi.listTemplates().then(response => setTemplates(response.data || [])).catch(error => toast.error(error.response?.data?.detail || 'Não foi possível carregar os modelos')).finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);
  const save = async (event) => {
    event.preventDefault();
    const default_sections = form.default_sections.split('\n').map(value => value.trim()).filter(Boolean);
    if (!default_sections.length) { toast.error('Informe ao menos uma seção padrão'); return; }
    try {
      setSaving(true);
      await documentsApi.createTemplate({ document_type: form.document_type, name: form.name, default_sections, settings: { company_name: form.company_name, footer_text: form.footer_text, primary_color: form.primary_color, body_color: form.body_color, font_family: form.font_family, heading_font_family: form.heading_font_family } });
      toast.success('Modelo publicado'); load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Não foi possível salvar o modelo'); }
    finally { setSaving(false); }
  };
  const uploadPaper = async (template, file) => {
    if (!file) return;
    try {
      setUploadingTemplateId(template.id);
      await documentsApi.uploadTemplateFile(template.id, file);
      toast.success('Papel/modelo enviado como nova versão do template');
      load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Não foi possível processar o modelo'); }
    finally { setUploadingTemplateId(null); }
  };
  return <div className="fixed inset-0 z-[65] bg-black/60 flex items-center justify-center p-4"><div className="card w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6">
    <div className="flex justify-between gap-3 mb-5"><div><h2 className="text-xl font-bold text-white">Modelos controlados</h2><p className="text-sm text-gray-400">Cada nome de modelo é uma opção independente; reutilizar o mesmo nome cria uma nova versão desse modelo.</p></div><button onClick={onClose} className="text-gray-400 hover:text-white"><X size={22} /></button></div>
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="space-y-3 text-gray-200">
        <label className="block text-sm">Tipo<select className="input mt-1" value={form.document_type} onChange={event => { const label = TEMPLATE_TYPES.find(([value]) => value === event.target.value)?.[1] || 'Documento'; setForm(value => ({ ...value, document_type: event.target.value, name: `Modelo de ${label}` })); }}>{TEMPLATE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="block text-sm">Nome do modelo<input required className="input mt-1" value={form.name} onChange={event => setForm(value => ({ ...value, name: event.target.value }))} placeholder="Ex.: POP Assistencial" /></label>
        <label className="block text-sm">Empresa no cabeçalho<input className="input mt-1" value={form.company_name} onChange={event => setForm(value => ({ ...value, company_name: event.target.value }))} placeholder="Sistema de Gestão da Qualidade" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">Cor dos títulos<input type="color" className="input mt-1 h-10 p-1" value={form.primary_color} onChange={event => setForm(value => ({ ...value, primary_color: event.target.value }))} /></label>
          <label className="block text-sm">Cor do texto<input type="color" className="input mt-1 h-10 p-1" value={form.body_color} onChange={event => setForm(value => ({ ...value, body_color: event.target.value }))} /></label>
          <label className="block text-sm">Fonte do texto<select className="input mt-1" value={form.font_family} onChange={event => setForm(value => ({ ...value, font_family: event.target.value }))}>{['Arial', 'Helvetica', 'Times New Roman', 'Georgia'].map(font => <option key={font}>{font}</option>)}</select></label>
          <label className="block text-sm">Fonte dos títulos<select className="input mt-1" value={form.heading_font_family} onChange={event => setForm(value => ({ ...value, heading_font_family: event.target.value }))}>{['Arial', 'Helvetica', 'Times New Roman', 'Georgia'].map(font => <option key={font}>{font}</option>)}</select></label>
        </div>
        <label className="block text-sm">Rodapé<input className="input mt-1" value={form.footer_text} onChange={event => setForm(value => ({ ...value, footer_text: event.target.value }))} /></label>
        <label className="block text-sm">Seções padrão <span className="text-gray-500">(uma por linha)</span><textarea className="input mt-1 min-h-44" value={form.default_sections} onChange={event => setForm(value => ({ ...value, default_sections: event.target.value }))} /></label>
        <button disabled={saving} className="btn-primary">{saving ? 'Salvando…' : 'Publicar modelo'}</button>
      </form>
      <section><h3 className="font-medium text-white mb-3">Modelos disponíveis</h3>{loading ? <Loader2 className="animate-spin" /> : templates.length === 0 ? <p className="text-gray-400">Os modelos padrão serão criados no primeiro documento estruturado.</p> : <div className="space-y-3">{templates.map(template => <div key={template.id} className="border border-gray-700 rounded p-3"><p className="text-white font-medium">{template.name} <span className="text-gray-400 font-normal">v{template.version_number}</span></p><p className="text-xs text-gray-400">{TEMPLATE_TYPES.find(([value]) => value === template.document_type)?.[1] || template.document_type} · {template.default_sections.join(' · ')}</p><p className="text-xs mt-2 text-gray-300">{template.source_file_name ? `Papel/modelo: ${template.source_file_name}` : 'Nenhum papel/modelo enviado'}</p><p className="text-xs mt-1 text-gray-400">PDF em qualquer dimensão; DOCX é convertido automaticamente para PDF.</p><label className="btn-secondary text-xs inline-flex mt-3 cursor-pointer">{uploadingTemplateId === template.id ? 'Processando…' : 'Enviar papel timbrado'}<input type="file" hidden accept=".pdf,.docx,.png" disabled={uploadingTemplateId === template.id} onChange={event => uploadPaper(template, event.target.files?.[0])} /></label></div>)}</div>}</section>
    </div>
  </div></div>;
};

const AiImportDialog = ({ onClose }) => {
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const startImport = async () => {
    if (!files.length || !consent) { toast.error('Selecione os arquivos e confirme o envio à IA.'); return; }
    try { setImporting(true); const response = await documentsApi.importLegacy(files, consent); setResult(response.data); toast.success('Importação concluída; revise os rascunhos antes de aprovar.'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Não foi possível concluir a importação'); }
    finally { setImporting(false); }
  };
  return <div className="fixed inset-0 z-[65] bg-black/60 flex items-center justify-center p-4"><div className="card w-full max-w-2xl p-6"><div className="flex justify-between gap-3"><div><h2 className="text-xl font-bold text-white">Importar documentos com IA</h2><p className="text-sm text-gray-400 mt-1">Aceita até 10 DOCX ou PDFs com texto pesquisável. PDFs escaneados exigem OCR e não são processados nesta etapa.</p></div><button onClick={onClose} className="text-gray-400 hover:text-white"><X size={22} /></button></div><div className="mt-5 space-y-4"><input type="file" multiple accept=".docx,.pdf" onChange={event => setFiles(Array.from(event.target.files || []).slice(0, 10))} className="input" /><p className="text-sm text-gray-400">{files.length ? files.map(file => file.name).join(', ') : 'Nenhum arquivo selecionado'}</p><label className="flex gap-2 text-sm text-gray-200 items-start"><input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} className="mt-1" />Confirmo que tenho autorização para enviar o conteúdo deste lote à IA Emergent para extrair um rascunho. Nenhum documento será aprovado automaticamente.</label><button disabled={importing || !consent || !files.length} onClick={startImport} className="btn-primary flex items-center gap-2">{importing && <Loader2 className="animate-spin" size={17} />} {importing ? 'Analisando…' : 'Criar rascunhos revisáveis'}</button>{result && <div className="border border-gray-700 rounded p-3 text-sm"><p className="font-medium text-white mb-2">Resultado do lote</p>{result.items.map(item => <div key={item.id} className="py-2 border-t border-gray-700 first:border-0"><span className={item.status === 'ready_review' ? 'text-green-400' : 'text-red-300'}>{item.original_file_name}: {item.status === 'ready_review' ? 'rascunho criado' : item.error_message || 'falhou'}</span>{item.warnings?.length > 0 && <p className="text-gray-400 mt-1">{item.warnings.join(' ')}</p>}</div>)}</div>}</div></div></div>;
};

const Pagination = ({ page, total, limit, onPageChange }) => {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
      <span className="text-sm text-gray-400">
        {total} registro{total !== 1 ? 's' : ''} · Página {page} de {totalPages}
      </span>
      <div className="flex gap-2">
        <button
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          className="btn-secondary text-sm px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <ChevronLeft size={14} /> Anterior
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="btn-secondary text-sm px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
        >
          Próxima <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default Documents;
